/**
 * Canales S/R — descubrimiento algorítmico de canales paralelos ascendentes/descendentes.
 *
 * A diferencia de Patrón de Ruptura (canales convergentes con lifecycle de trade),
 * este motor busca canales PARALELOS (tendencia) probando combinaciones de pares
 * de pivotes, puntuando la contención del precio dentro del canal, y monitoreando
 * rupturas/reacciones de límites sin TP/SL automáticos (motor standalone_detect).
 *
 * Puerto 1:1 de `backend/app/core/canales_sr.py` — misma fórmula, mismos nombres de campos.
 */

import BadgeComponent from './Badge.jsx'
import PanelComponent from './Panel.jsx'

// ── Constantes del motor (no negociables según especificación) ─────────────────
const MAX_PIVOT_BUFFER = 40
const MAX_RECENT_PIVOTS = 8
const MAX_PRECEDING_PIVOTS = 5
const MAX_QUALITY_BARS = 300
const CONTAIN_TOL_ATR = 0.05
const REACT_TOL_ATR = 0.12
const CROSS_TOL_ATR = 1.5
const REPLACE_RATIO = 0.7

// ── Helpers ────────────────────────────────────────────────────────────────────

function calcATR(candles, period) {
  const n = candles.length
  const tr = candles.map((c, i) =>
    i === 0
      ? c.high - c.low
      : Math.max(c.high - c.low, Math.abs(c.high - candles[i - 1].close), Math.abs(c.low - candles[i - 1].close))
  )
  const out = new Array(n).fill(null)
  if (n < period) return out
  let sum = 0
  for (let i = 0; i < period; i++) sum += tr[i]
  out[period - 1] = sum / period
  for (let i = period; i < n; i++) out[i] = (out[i - 1] * (period - 1) + tr[i]) / period
  return out
}

function lineValue(x1, y1, x2, y2, x) {
  const dx = x2 - x1
  if (dx === 0) return y1
  return y1 + (y2 - y1) * (x - x1) / dx
}

function searchChannel(baseIdx, baseVal, oppIdx, oppVal, ascending, minChannelBars, maxChannelBars, atr, currentI, low, high) {
  const m = baseIdx.length
  if (m < 2) return null

  let best = null
  const recentN = Math.min(MAX_RECENT_PIVOTS, m)

  for (let a = 0; a < recentN; a++) {
    const posI = m - 1 - a
    const idxI = baseIdx[posI]
    const valI = baseVal[posI]

    for (let b = 1; b <= MAX_PRECEDING_PIVOTS; b++) {
      const posJ = posI - b
      if (posJ < 0) break
      const idxJ = baseIdx[posJ]
      const valJ = baseVal[posJ]

      const span = idxI - idxJ
      if (span < minChannelBars || span > maxChannelBars) continue
      if (ascending && !(valI > valJ)) continue
      if (!ascending && !(valI < valJ)) continue

      let bestOffset = null
      for (let k = 0; k < oppIdx.length; k++) {
        const oi = oppIdx[k]
        if (oi < idxJ || oi > idxI) continue
        const ov = oppVal[k]
        const baseAt = lineValue(idxJ, valJ, idxI, valI, oi)
        const offset = ascending ? (ov - baseAt) : (baseAt - ov)
        if (bestOffset === null || offset > bestOffset) bestOffset = offset
      }

      if (bestOffset === null || bestOffset <= 0) continue
      const offset = bestOffset

      const startBar = Math.max(idxJ, currentI - MAX_QUALITY_BARS + 1)
      const tol = CONTAIN_TOL_ATR * atr
      let contained = 0
      let total = 0
      for (let bi = startBar; bi <= currentI; bi++) {
        const baseAt = lineValue(idxJ, valJ, idxI, valI, bi)
        let lower, upper
        if (ascending) { lower = baseAt; upper = baseAt + offset }
        else { lower = baseAt - offset; upper = baseAt }
        if (low[bi] >= lower - tol && high[bi] <= upper + tol) contained++
        total++
      }
      const quality = total > 0 ? contained / total : 0

      if (best === null || quality > best.quality) {
        best = { x1: idxJ, y1: valJ, x2: idxI, y2: valI, offset, quality }
      }
    }
  }

  return best
}

function maybeReplaceChannel(current, candidate, channelQuality, ascending, currentI, closeNow) {
  if (candidate === null || candidate.quality < channelQuality) return current

  if (current === null) {
    candidate.brokenBull = false
    candidate.brokenBear = false
    return candidate
  }

  const samePivots = candidate.x1 === current.x1 && candidate.x2 === current.x2
  if (samePivots) {
    candidate.brokenBull = current.brokenBull
    candidate.brokenBear = current.brokenBear
    return candidate
  }

  if (candidate.quality >= REPLACE_RATIO * current.quality) {
    const baseNow = lineValue(candidate.x1, candidate.y1, candidate.x2, candidate.y2, currentI)
    const offset = candidate.offset
    let lower, upper
    if (ascending) { lower = baseNow; upper = baseNow + offset }
    else { lower = baseNow - offset; upper = baseNow }
    const inside = lower <= closeNow && closeNow <= upper
    candidate.brokenBull = !inside
    candidate.brokenBear = !inside
    return candidate
  }

  return current
}

function emptyResult() {
  return {
    upChannel: null,
    downChannel: null,
    signals: {
      buy: false, sell: false,
      breakoutBull: false, breakoutBear: false,
      reactSup: false, reactRes: false,
    },
    signalLevels: null,
    breakoutLabels: [],
    dashboard: {
      trend: 'Neutral', signal: 'Wait', strength: '—',
      activeChannels: 0, quality: '—', tf: '',
    },
    pivots: { highs: [], lows: [] },
  }
}

// ── detect ─────────────────────────────────────────────────────────────────────

function detect(candles, config) {
  const cfg = { ...defaultConfig, ...(config || {}) }
  if (!candles || candles.length === 0) return emptyResult()

  const n = candles.length
  const pivotLen = cfg.pivotLen
  const atrLen = cfg.atrLen
  const minChannelBars = cfg.minChannelBars
  const maxChannelBars = cfg.maxChannelBars
  const channelQuality = cfg.channelQuality

  const warmup = Math.max(pivotLen * 2 + 1, atrLen + 5, 30)
  if (n < warmup + 5) return emptyResult()

  const time = candles.map(c => c.time)
  const open_ = candles.map(c => c.open)
  const high = candles.map(c => c.high)
  const low = candles.map(c => c.low)
  const close = candles.map(c => c.close)

  const atrs = calcATR(candles, atrLen)

  const pivotHighsIdx = []
  const pivotHighsVal = []
  const pivotLowsIdx = []
  const pivotLowsVal = []

  let upChannel = null
  let downChannel = null

  const breakoutLabels = []

  let sigBuy = false, sigSell = false
  let sigBreakoutBull = false, sigBreakoutBear = false
  let sigReactSup = false, sigReactRes = false
  let sigSignalLevels = null
  let lastSignalQuality = null

  for (let i = warmup; i < n; i++) {
    let atr = atrs[i]
    if (atr == null || isNaN(atr) || atr <= 0) atr = 1e-9

    let newPivot = false
    if (i >= pivotLen && i + pivotLen < n) {
      let maxH = -Infinity, minL = Infinity
      for (let j = i - pivotLen; j <= i + pivotLen; j++) {
        if (high[j] > maxH) maxH = high[j]
        if (low[j] < minL) minL = low[j]
      }
      if (high[i] === maxH) {
        pivotHighsIdx.push(i)
        pivotHighsVal.push(high[i])
        if (pivotHighsIdx.length > MAX_PIVOT_BUFFER) { pivotHighsIdx.shift(); pivotHighsVal.shift() }
        newPivot = true
      }
      if (low[i] === minL) {
        pivotLowsIdx.push(i)
        pivotLowsVal.push(low[i])
        if (pivotLowsIdx.length > MAX_PIVOT_BUFFER) { pivotLowsIdx.shift(); pivotLowsVal.shift() }
        newPivot = true
      }
    }

    if (newPivot && pivotLowsIdx.length >= 2 && pivotHighsIdx.length >= 2) {
      const candUp = searchChannel(
        pivotLowsIdx, pivotLowsVal, pivotHighsIdx, pivotHighsVal,
        true, minChannelBars, maxChannelBars, atr, i, low, high,
      )
      upChannel = maybeReplaceChannel(upChannel, candUp, channelQuality, true, i, close[i])

      const candDown = searchChannel(
        pivotHighsIdx, pivotHighsVal, pivotLowsIdx, pivotLowsVal,
        false, minChannelBars, maxChannelBars, atr, i, low, high,
      )
      downChannel = maybeReplaceChannel(downChannel, candDown, channelQuality, false, i, close[i])
    }

    if (upChannel && i - upChannel.x2 > maxChannelBars) upChannel = null
    if (downChannel && i - downChannel.x2 > maxChannelBars) downChannel = null

    let upBreakRes = false, upBreakSup = false, upReactSup = false, upReactRes = false
    let dnBreakRes = false, dnBreakSup = false, dnReactSup = false, dnReactRes = false
    let upBaseNow = null, upParNow = null, dnBaseNow = null, dnParNow = null

    if (i >= 1) {
      const crossTol = CROSS_TOL_ATR * atr
      const reactTol = REACT_TOL_ATR * atr

      if (upChannel) {
        upBaseNow = lineValue(upChannel.x1, upChannel.y1, upChannel.x2, upChannel.y2, i)
        upParNow = upBaseNow + upChannel.offset
        const basePrev = lineValue(upChannel.x1, upChannel.y1, upChannel.x2, upChannel.y2, i - 1)
        const parPrev = basePrev + upChannel.offset

        if (!upChannel.brokenBull && close[i] > upParNow && Math.abs(close[i - 1] - parPrev) <= crossTol) {
          upBreakRes = true
          upChannel.brokenBull = true
          breakoutLabels.push({ dir: 'bull', bar: i, time: time[i], price: close[i], channel: 'up' })
        }
        if (!upChannel.brokenBear && close[i] < upBaseNow && Math.abs(close[i - 1] - basePrev) <= crossTol) {
          upBreakSup = true
          upChannel.brokenBear = true
          breakoutLabels.push({ dir: 'bear', bar: i, time: time[i], price: close[i], channel: 'up' })
        }

        upReactSup = Math.abs(low[i] - upBaseNow) <= reactTol && close[i] > upBaseNow && open_[i] > upBaseNow
        upReactRes = Math.abs(high[i] - upParNow) <= reactTol && close[i] < upParNow && open_[i] < upParNow
      }

      if (downChannel) {
        dnBaseNow = lineValue(downChannel.x1, downChannel.y1, downChannel.x2, downChannel.y2, i)
        dnParNow = dnBaseNow - downChannel.offset
        const basePrev = lineValue(downChannel.x1, downChannel.y1, downChannel.x2, downChannel.y2, i - 1)
        const parPrev = basePrev - downChannel.offset

        if (!downChannel.brokenBull && close[i] > dnBaseNow && Math.abs(close[i - 1] - basePrev) <= crossTol) {
          dnBreakRes = true
          downChannel.brokenBull = true
          breakoutLabels.push({ dir: 'bull', bar: i, time: time[i], price: close[i], channel: 'down' })
        }
        if (!downChannel.brokenBear && close[i] < dnParNow && Math.abs(close[i - 1] - parPrev) <= crossTol) {
          dnBreakSup = true
          downChannel.brokenBear = true
          breakoutLabels.push({ dir: 'bear', bar: i, time: time[i], price: close[i], channel: 'down' })
        }

        dnReactSup = Math.abs(low[i] - dnParNow) <= reactTol && close[i] > dnParNow && open_[i] > dnParNow
        dnReactRes = Math.abs(high[i] - dnBaseNow) <= reactTol && close[i] < dnBaseNow && open_[i] < dnBaseNow
      }
    }

    const buy = upReactSup || dnBreakRes
    const sell = upBreakSup || dnReactRes
    const breakoutBull = upBreakRes || dnBreakRes
    const breakoutBear = upBreakSup || dnBreakSup
    const reactSup = upReactSup || dnReactSup
    const reactRes = upReactRes || dnReactRes

    sigBuy = buy; sigSell = sell
    sigBreakoutBull = breakoutBull; sigBreakoutBear = breakoutBear
    sigReactSup = reactSup; sigReactRes = reactRes

    // Niveles de referencia del evento que generó buy/sell en ESTA barra,
    // atribuidos al canal que realmente lo disparó (independiente de
    // showUp/showDown, que solo afectan la visualización). Ver
    // backend/app/core/canales_sr.py para la semántica completa.
    let signalLevels = null
    if (upReactSup) {
      signalLevels = { side: 'up', entry: close[i], sl: upBaseNow, tp1: upParNow }
    } else if (dnBreakRes) {
      const offset = dnBaseNow - dnParNow
      signalLevels = { side: 'down', entry: close[i], sl: dnBaseNow, tp1: close[i] + offset }
    } else if (upBreakSup) {
      const offset = upParNow - upBaseNow
      signalLevels = { side: 'up', entry: close[i], sl: upBaseNow, tp1: close[i] - offset }
    } else if (dnReactRes) {
      signalLevels = { side: 'down', entry: close[i], sl: dnBaseNow, tp1: dnParNow }
    }
    sigSignalLevels = signalLevels

    if (buy || sell) {
      const candidatesQ = [upChannel, downChannel].filter(Boolean).map(c => c.quality)
      lastSignalQuality = candidatesQ.length ? Math.max(...candidatesQ) : null
    }
  }

  const showUp = cfg.showUp !== false
  const showDown = cfg.showDown !== false
  const lastI = n - 1

  const publicChannel = (ch, ascending) => {
    if (!ch) return null
    const baseNow = lineValue(ch.x1, ch.y1, ch.x2, ch.y2, lastI)
    const offset = ch.offset
    const basePrice = baseNow
    const parallelPrice = ascending ? baseNow + offset : baseNow - offset
    return {
      active: true,
      x1: ch.x1, y1: ch.y1, x2: ch.x2, y2: ch.y2,
      x1Time: time[ch.x1], x2Time: time[ch.x2],
      offset, quality: ch.quality,
      basePrice, parallelPrice,
      midPrice: (basePrice + parallelPrice) / 2,
    }
  }

  const upOut = showUp ? publicChannel(upChannel, true) : null
  const downOut = showDown ? publicChannel(downChannel, false) : null

  const activeChannels = (upOut ? 1 : 0) + (downOut ? 1 : 0)

  let trend = 'Neutral'
  if (upOut && !downOut) trend = 'Bullish'
  else if (downOut && !upOut) trend = 'Bearish'

  const signalLabel = sigBuy ? 'Buy' : sigSell ? 'Sell' : 'Wait'

  let qualityForStrength = lastSignalQuality
  if (qualityForStrength == null) {
    const qualities = [upOut, downOut].filter(Boolean).map(c => c.quality)
    qualityForStrength = qualities.length ? Math.max(...qualities) : null
  }

  let strength = '—'
  let qualityStr = '—'
  if (qualityForStrength != null) {
    strength = qualityForStrength >= 0.7 ? 'Strong' : qualityForStrength >= 0.4 ? 'Medium' : 'Weak'
    qualityStr = `${(qualityForStrength * 100).toFixed(1)}%`
  }

  const dashboard = {
    trend, signal: signalLabel, strength,
    activeChannels, quality: qualityStr,
    tf: cfg.tf ?? '',
  }

  const pivots = {
    highs: pivotHighsIdx.map((idx, k) => ({ price: pivotHighsVal[k], bar: idx })),
    lows: pivotLowsIdx.map((idx, k) => ({ price: pivotLowsVal[k], bar: idx })),
  }

  return {
    upChannel: upOut,
    downChannel: downOut,
    signals: {
      buy: sigBuy, sell: sigSell,
      breakoutBull: sigBreakoutBull, breakoutBear: sigBreakoutBear,
      reactSup: sigReactSup, reactRes: sigReactRes,
    },
    signalLevels: sigSignalLevels,
    breakoutLabels,
    dashboard,
    pivots,
  }
}

// ── render (lightweight-charts primitives) ─────────────────────────────────────

function lineStyleToNumber(style) {
  return style === 'dashed' ? 2 : style === 'dotted' ? 1 : 0
}

function render(chart, candleSeries, result, config, api) {
  if (!result) return

  const {
    showMidline = true,
    extend = 'right',
    showBreakoutLbl = true,
    baseWidth = 2, baseStyle = 'solid',
    parWidth = 2, parStyle = 'solid',
    midWidth = 1, midStyle = 'dashed',
    bullColor = '#00E676',
    bearColor = '#FF5252',
  } = config

  let rangeFrom = null, rangeTo = null
  try {
    const range = chart.timeScale().getVisibleRange()
    if (range) { rangeFrom = range.from; rangeTo = range.to }
  } catch { /* noop */ }

  const common = {
    lastValueVisible: false,
    priceLineVisible: false,
    crosshairMarkerVisible: false,
  }

  // Evalúa la recta (x1Time,y1)-(x2Time,y2) en el espacio tiempo-precio (no bar index)
  const valueAtTime = (x1Time, y1, x2Time, y2, t) => {
    const dt = x2Time - x1Time
    if (dt === 0) return y1
    return y1 + (y2 - y1) * (t - x1Time) / dt
  }

  // En timeframes diarios+ los tiempos son strings 'YYYY-MM-DD' (no epoch):
  // la resta numérica de arriba da NaN, y extender la línea hasta el borde
  // visible (rangeFrom/rangeTo, también strings ahí) requeriría aritmética de
  // fechas que no tenemos. En ese caso no extendemos: solo se dibuja el
  // segmento real x1Time..x2Time.
  const canExtend = typeof (rangeFrom ?? rangeTo) !== 'string'

  const drawLine = (x1Time, y1, x2Time, y2, color, width, style) => {
    const startTime = canExtend && extend === 'both' && rangeFrom != null ? rangeFrom : x1Time
    const endTime = canExtend && extend !== 'none' && rangeTo != null ? rangeTo : x2Time
    const points = [
      { time: startTime, value: valueAtTime(x1Time, y1, x2Time, y2, startTime) },
      { time: x2Time, value: y2 },
    ]
    if (endTime !== x2Time) {
      points.push({ time: endTime, value: valueAtTime(x1Time, y1, x2Time, y2, endTime) })
    }
    const series = api.addSeries(c => c.addLineSeries({
      ...common, color, lineWidth: width, lineStyle: lineStyleToNumber(style),
    }))
    if (series) series.setData(points)
  }

  const drawChannel = (ch, color) => {
    if (!ch) return
    const offset = ch.parallelPrice - ch.basePrice

    drawLine(ch.x1Time, ch.y1, ch.x2Time, ch.y2, color, baseWidth, baseStyle)
    drawLine(ch.x1Time, ch.y1 + offset, ch.x2Time, ch.y2 + offset, color, parWidth, parStyle)

    if (showMidline) {
      drawLine(ch.x1Time, ch.y1 + offset / 2, ch.x2Time, ch.y2 + offset / 2, color, midWidth, midStyle)
    }
  }

  drawChannel(result.upChannel, bullColor)
  drawChannel(result.downChannel, bearColor)

  if (showBreakoutLbl) {
    for (const lbl of result.breakoutLabels.slice(-40)) {
      api.addLabel({
        time: lbl.time,
        text: lbl.dir === 'bull' ? 'Breakout ▲' : 'Breakout ▼',
        color: lbl.dir === 'bull' ? bullColor : bearColor,
        isBull: lbl.dir === 'bull',
        fallbackPrice: lbl.price,
      })
    }
  }
}

// ── Default config ─────────────────────────────────────────────────────────────

const defaultConfig = {
  _version: '1.0',
  // Main
  pivotLen: 21,
  atrLen: 14,
  minChannelBars: 10,
  maxChannelBars: 400,
  channelQuality: 0.55,
  // Visual
  showMidline: true,
  showFill: true,
  showWatermark: true,
  extend: 'right',
  deletePrev: true,
  showBreakoutLbl: true,
  // Channel toggles
  showUp: true,
  showDown: true,
  // Style
  baseWidth: 2,
  baseStyle: 'solid',
  parWidth: 2,
  parStyle: 'solid',
  midWidth: 1,
  midStyle: 'dashed',
  // Colors
  bullColor: '#00E676',
  bearColor: '#FF5252',
  fillTransp: 95,
  // Dashboard
  showDash: false,
  dashPos: 'top_right',
  // Alerts
  alertBreak: true,
  alertBreakout: true,
  alertReact: true,
  webhook: false,
}

export default {
  id: 'canales_sr',
  name: 'Canales S/R',
  version: '1.0',
  defaultConfig,
  detect,
  render,
  BadgeComponent,
  PanelComponent,
}
